import { relativeTime } from '../lib/relative-time';

export type AuditRow = {
  id: number | string;
  tenant_id: string;
  user_id: string | null;
  action: string;
  payload: Record<string, unknown> | null;
  created_at: string;
  user_email?: string | null;
};

type ActionPill = {
  label: string;
  classes: string;
};

function actionPill(action: string): ActionPill {
  switch (action) {
    case 'tenant.created':
      return { label: action, classes: 'bg-sky-900/40 text-sky-300 border border-sky-700/40' };
    case 'wrap.updated':
      return { label: action, classes: 'bg-amber-900/40 text-amber-300 border border-amber-700/40' };
    case 'ai-cto.message':
      return { label: action, classes: 'bg-slate-700/40 text-slate-300 border border-slate-600/40' };
    default:
      return { label: action, classes: 'bg-slate-800/40 text-slate-400 border border-slate-700/30' };
  }
}

function payloadPreview(payload: Record<string, unknown> | null): string {
  if (!payload) return '';
  const str = JSON.stringify(payload);
  return str.length > 80 ? str.slice(0, 77) + '…' : str;
}

type AuditTableProps = {
  rows: AuditRow[];
  onLoadOlder: () => void;
  isLoadingMore: boolean;
  hasMore: boolean;
};

export function AuditTable({ rows, onLoadOlder, isLoadingMore, hasMore }: AuditTableProps) {
  if (rows.length === 0) {
    return <p className="text-sm text-slate-400">No activity yet.</p>;
  }

  return (
    <div>
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-xs uppercase tracking-widest text-slate-500 border-b border-slate-800">
            <th className="pb-2 pr-4 font-normal">Time</th>
            <th className="pb-2 pr-4 font-normal">Action</th>
            <th className="pb-2 pr-4 font-normal">User</th>
            <th className="pb-2 font-normal">Payload</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const pill = actionPill(row.action);
            return (
              <tr key={row.id} className="border-b border-slate-800/50 hover:bg-slate-900/30">
                <td className="py-3 pr-4 align-top">
                  <span
                    className="text-slate-300 cursor-default"
                    title={row.created_at}
                  >
                    {relativeTime(row.created_at)}
                  </span>
                </td>
                <td className="py-3 pr-4 align-top">
                  <span className={`inline-block rounded px-2 py-0.5 text-xs font-mono ${pill.classes}`}>
                    {pill.label}
                  </span>
                </td>
                <td className="py-3 pr-4 align-top text-slate-400">
                  {row.user_email ?? (row.user_id ? row.user_id : 'system')}
                </td>
                <td className="py-3 align-top font-mono text-xs text-slate-500 max-w-xs truncate">
                  {payloadPreview(row.payload)}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {hasMore && (
        <div className="mt-4">
          <button
            onClick={onLoadOlder}
            disabled={isLoadingMore}
            className="text-sm text-sky-400 hover:text-sky-300 disabled:opacity-50"
          >
            {isLoadingMore ? 'Loading…' : 'Load older'}
          </button>
        </div>
      )}
    </div>
  );
}
